import type { FilterType } from '@/modules/analytics/constants/metricsCatalog';

// Operators are named after the user-facing labels (is, is_not, ...). Each
// maps to one shape of OData clause. The set is intentionally narrow — these
// are the operators the analytics service can both accept AND that we can
// round-trip parse back into structured form.
export type FilterOperator =
  | 'is'
  | 'is_not'
  | 'is_any_of'
  | 'is_not_any_of'
  | 'is_set'
  | 'is_not_set'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'not_starts_with'
  | 'ends_with'
  | 'not_ends_with'
  | 'gt'
  | 'ge'
  | 'lt'
  | 'le';

export type FilterChip = {
  type: FilterType;
  operator: FilterOperator;
  // Values are kept as strings so the editor can store partial / unparsed
  // input. Coercion to number / boolean happens at serialize time.
  values: string[];
};

export type ReportFilters = Record<string, FilterChip>;

const STRING_TYPES: ReadonlySet<FilterType> = new Set(['string', 'uuid']);

// Operators that don't take values (col eq null / col ne null shape).
const NO_VALUE_OPS: ReadonlySet<FilterOperator> = new Set(['is_set', 'is_not_set']);

const MULTI_VALUE_OPS: ReadonlySet<FilterOperator> = new Set(['is_any_of', 'is_not_any_of']);

export const isMultiValueOperator = (op: FilterOperator): boolean => MULTI_VALUE_OPS.has(op);

const RATING_COLUMNS: ReadonlySet<string> = new Set(['csat_brain', 'csat_human_agent']);

export const isRatingColumn = (column: string): boolean => RATING_COLUMNS.has(column);

export const RATING_OPERATORS: FilterOperator[] = ['is_any_of', 'gt', 'lt'];
export const isNoValueOperator = (op: FilterOperator): boolean => NO_VALUE_OPS.has(op);

export const operatorKeepsValues = (
  column: string,
  from: FilterOperator,
  to: FilterOperator
): boolean => {
  if (isNoValueOperator(to)) {
    return false;
  }
  if (isRatingColumn(column)) {
    return to === from;
  }
  return true;
};

// Lists the operators that are valid for each filter type. The UI uses this to
// build the operator dropdown per chip and to fall back when a type changes.
export const getOperatorsForType = (type: FilterType): FilterOperator[] => {
  if (type === 'boolean') {
    return ['is', 'is_set', 'is_not_set'];
  }
  if (type === 'number') {
    return [
      'is',
      'is_not',
      'is_any_of',
      'is_not_any_of',
      'gt',
      'ge',
      'lt',
      'le',
      'is_set',
      'is_not_set',
    ];
  }
  if (type === 'string') {
    return [
      'is',
      'is_not',
      'is_any_of',
      'is_not_any_of',
      'contains',
      'not_contains',
      'starts_with',
      'not_starts_with',
      'ends_with',
      'not_ends_with',
      'is_set',
      'is_not_set',
    ];
  }
  // uuid
  return ['is', 'is_not', 'is_any_of', 'is_not_any_of', 'is_set', 'is_not_set'];
};

export const getDefaultOperatorForType = (_type: FilterType): FilterOperator => 'is';

const escapeString = (s: string): string => `'${s.replace(/'/g, "''")}'`;

// Renders one stored value into its OData literal form per type. Number values
// that don't parse cleanly are dropped.
const renderValue = (raw: string, type: FilterType): string | undefined => {
  if (type === 'boolean') {
    if (raw === 'true' || raw === 'false') {
      return raw;
    }
    return undefined;
  }
  if (type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? String(n) : undefined;
  }
  return escapeString(raw);
};

const renderValueList = (values: string[], type: FilterType): string[] =>
  values.map((v) => renderValue(v, type)).filter((v): v is string => v !== undefined);

// Maps an active filter chip to a single OData clause. Returns undefined when
// the chip is in an incomplete state (e.g. operator needs values but none yet).
const renderClause = (column: string, chip: FilterChip): string | undefined => {
  if (NO_VALUE_OPS.has(chip.operator)) {
    return chip.operator === 'is_set' ? `${column} ne null` : `${column} eq null`;
  }
  const rendered = renderValueList(chip.values, chip.type);
  if (rendered.length === 0) {
    return undefined;
  }

  switch (chip.operator) {
    case 'is':
      return `${column} eq ${rendered[0]}`;
    case 'is_not':
      return `${column} ne ${rendered[0]}`;
    case 'is_any_of':
      if (rendered.length === 1) {
        return `${column} eq ${rendered[0]}`;
      }
      return `${column} in (${rendered.join(', ')})`;
    case 'is_not_any_of':
      if (rendered.length === 1) {
        return `${column} ne ${rendered[0]}`;
      }
      return `not ${column} in (${rendered.join(', ')})`;
    case 'contains':
      return `contains(${column}, ${rendered[0]})`;
    case 'not_contains':
      return `not contains(${column}, ${rendered[0]})`;
    case 'starts_with':
      return `startswith(${column}, ${rendered[0]})`;
    case 'not_starts_with':
      return `not startswith(${column}, ${rendered[0]})`;
    case 'ends_with':
      return `endswith(${column}, ${rendered[0]})`;
    case 'not_ends_with':
      return `not endswith(${column}, ${rendered[0]})`;
    case 'gt':
      return `${column} gt ${rendered[0]}`;
    case 'ge':
      return `${column} ge ${rendered[0]}`;
    case 'lt':
      return `${column} lt ${rendered[0]}`;
    case 'le':
      return `${column} le ${rendered[0]}`;
    default:
      return undefined;
  }
};

// Serializes the structured filter map into an OData expression matching the
// grammar the analytics service accepts (see analytics/api/filter/parser.go).
// We only emit AND-joined clauses — the shape we can round-trip parse.
export const serializeFilters = (filters: ReportFilters): string | undefined => {
  const clauses: string[] = [];
  for (const [column, chip] of Object.entries(filters)) {
    const clause = renderClause(column, chip);
    if (clause) {
      clauses.push(clause);
    }
  }
  return clauses.length === 0 ? undefined : clauses.join(' and ');
};

// Splits a top-level clause list on " and ", respecting parens and string
// literals so an `in (a, b)` list or a quoted comma isn't broken apart.
const splitAndClauses = (input: string): string[] => {
  const out: string[] = [];
  let depth = 0;
  let inString = false;
  let start = 0;
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    if (ch === "'") {
      if (inString && input[i + 1] === "'") {
        i += 1;
        continue;
      }
      inString = !inString;
      continue;
    }
    if (inString) {
      continue;
    }
    if (ch === '(') {
      depth += 1;
      continue;
    }
    if (ch === ')') {
      depth -= 1;
      continue;
    }
    if (depth === 0 && input.slice(i, i + 5).toLowerCase() === ' and ') {
      out.push(input.slice(start, i));
      start = i + 5;
      i += 4;
    }
  }
  out.push(input.slice(start));
  return out.map((s) => s.trim()).filter(Boolean);
};

const unquoteString = (literal: string): string => literal.slice(1, -1).replace(/''/g, "'");

// Splits the inside of an `in (...)` list, respecting quoted strings so a
// comma inside 'foo, bar' isn't treated as a separator.
const splitInList = (inside: string): string[] => {
  const parts: string[] = [];
  let inString = false;
  let start = 0;
  for (let i = 0; i < inside.length; i += 1) {
    const ch = inside[i];
    if (ch === "'") {
      if (inString && inside[i + 1] === "'") {
        i += 1;
        continue;
      }
      inString = !inString;
      continue;
    }
    if (inString) {
      continue;
    }
    if (ch === ',') {
      parts.push(inside.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(inside.slice(start));
  return parts.map((s) => s.trim()).filter(Boolean);
};

// Parses one literal token back to a stored string value, normalizing the
// quoting so the round-trip is faithful.
const parseLiteralToStored = (literal: string, type: FilterType): string | undefined => {
  if (type === 'boolean') {
    return literal === 'true' || literal === 'false' ? literal : undefined;
  }
  if (type === 'number') {
    const n = Number(literal);
    return Number.isFinite(n) ? String(n) : undefined;
  }
  if (literal.startsWith("'") && literal.endsWith("'")) {
    return unquoteString(literal);
  }
  return undefined;
};

// Recognized comparison operators (left → operator token in OData).
const COMPARISON_OPS: Record<string, FilterOperator> = {
  eq: 'is',
  ne: 'is_not',
  gt: 'gt',
  ge: 'ge',
  lt: 'lt',
  le: 'le',
};

const tryParseNullClause = (
  clause: string,
  columnTypes: Record<string, FilterType>
): [string, FilterChip] | undefined => {
  const m = clause.match(/^(\w+)\s+(eq|ne)\s+null$/i);
  if (!m) {
    return undefined;
  }
  const [, column, opToken] = m;
  const type = columnTypes[column];
  if (!type) {
    return undefined;
  }
  const operator: FilterOperator = opToken.toLowerCase() === 'eq' ? 'is_not_set' : 'is_set';
  return [column, { type, operator, values: [] }];
};

const tryParseComparison = (
  clause: string,
  columnTypes: Record<string, FilterType>
): [string, FilterChip] | undefined => {
  const m = clause.match(/^(\w+)\s+(eq|ne|gt|ge|lt|le)\s+(.+)$/i);
  if (!m) {
    return undefined;
  }
  const [, column, opTokenRaw, literal] = m;
  const type = columnTypes[column];
  if (!type) {
    return undefined;
  }
  const opToken = opTokenRaw.toLowerCase();
  // `null` literals are handled separately; if we got here the literal is real.
  if (literal.trim().toLowerCase() === 'null') {
    return undefined;
  }
  const stored = parseLiteralToStored(literal.trim(), type);
  if (stored === undefined) {
    return undefined;
  }
  const operator = COMPARISON_OPS[opToken];
  // gt/ge/lt/le only make sense for number — fall through if mismatched.
  if (
    (operator === 'gt' || operator === 'ge' || operator === 'lt' || operator === 'le') &&
    type !== 'number'
  ) {
    return undefined;
  }
  return [
    column,
    {
      type,
      operator: operator === 'is' && isRatingColumn(column) ? 'is_any_of' : operator,
      values: [stored],
    },
  ];
};

const tryParseIn = (
  clause: string,
  columnTypes: Record<string, FilterType>
): [string, FilterChip] | undefined => {
  // Accept both `col in (...)` and `not col in (...)` — the `not` prefix is a
  // unary boolean prefix at the parser level (see analytics/api/filter/parser.go
  // `parseNot`), so it lives just before the column name in the printed form.
  const m = clause.match(/^(not\s+)?(\w+)\s+in\s+\((.*)\)$/i);
  if (!m) {
    return undefined;
  }
  const [, notPrefix, column, inside] = m;
  const type = columnTypes[column];
  if (!type || type === 'boolean') {
    return undefined;
  }
  const stored = splitInList(inside)
    .map((lit) => parseLiteralToStored(lit, type))
    .filter((v): v is string => v !== undefined);
  if (stored.length === 0) {
    return undefined;
  }
  const operator: FilterOperator = notPrefix ? 'is_not_any_of' : 'is_any_of';
  return [column, { type, operator, values: stored }];
};

// Maps the OData function name to the positive (non-negated) operator. The
// `not` prefix flips each to its `not_*` counterpart at parse time.
const STRING_FUNCTIONS: Record<string, FilterOperator> = {
  contains: 'contains',
  startswith: 'starts_with',
  endswith: 'ends_with',
};

const NEGATED_STRING_FUNCTIONS: Record<string, FilterOperator> = {
  contains: 'not_contains',
  startswith: 'not_starts_with',
  endswith: 'not_ends_with',
};

const tryParseStringFunction = (
  clause: string,
  columnTypes: Record<string, FilterType>
): [string, FilterChip] | undefined => {
  const m = clause.match(
    /^(not\s+)?(contains|startswith|endswith)\(\s*(\w+)\s*,\s*('(?:[^']|'')*')\s*\)$/i
  );
  if (!m) {
    return undefined;
  }
  const [, notPrefix, fnRaw, column, literal] = m;
  const type = columnTypes[column];
  if (!type || !STRING_TYPES.has(type)) {
    return undefined;
  }
  const fn = fnRaw.toLowerCase();
  const operator = (notPrefix ? NEGATED_STRING_FUNCTIONS : STRING_FUNCTIONS)[fn];
  return [column, { type, operator, values: [unquoteString(literal)] }];
};

// Best-effort parser for the flat AND-of-(eq|ne|in|null|contains|startswith|
// endswith|gt|ge|lt|le), each optionally prefixed with `not`, that
// serializeFilters emits. Unknown columns and unrecognised clauses are
// silently ignored — the goal is to restore editor state, not validate the
// upstream filter string.
export const parseFilters = (
  expression: string | undefined,
  columnTypes: Record<string, FilterType>
): ReportFilters => {
  if (!expression) {
    return {};
  }
  const filters: ReportFilters = {};
  for (const clause of splitAndClauses(expression)) {
    const parsed =
      tryParseNullClause(clause, columnTypes) ??
      tryParseIn(clause, columnTypes) ??
      tryParseStringFunction(clause, columnTypes) ??
      tryParseComparison(clause, columnTypes);
    if (parsed) {
      const [column, chip] = parsed;
      filters[column] = chip;
    }
  }
  return filters;
};
